import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Edit, Save, X, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChapterView = ({ user }) => {
  const { chapterId } = useParams();
  const navigate = useNavigate();
  const [chapter, setChapter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [editContent, setEditContent] = useState("");

  useEffect(() => {
    loadChapter();
  }, [chapterId]);

  const loadChapter = async () => {
    try {
      const response = await axios.get(`${API}/chapters/${chapterId}`);
      setChapter(response.data);
      setEditContent(response.data.content);
    } catch (error) {
      toast.error("Failed to load chapter");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/chapters/${chapterId}`, { content: editContent });
      toast.success("Chapter saved");
      setEditing(false);
      loadChapter();
    } catch (error) {
      toast.error("Failed to save chapter");
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm("Are you sure? This will replace the current chapter content.")) return;

    setRegenerating(true);
    try {
      const response = await axios.post(`${API}/chapters/${chapterId}/regenerate`, {});
      setChapter(response.data);
      setEditContent(response.data.content);
      toast.success("Chapter regenerated");
    } catch (error) {
      toast.error("Failed to regenerate chapter");
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-stone-100">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-stone-50 to-neutral-100">
      {/* Header */}
      <div className="border-b border-stone-200 bg-white/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                data-testid="back-to-book-btn"
                variant="ghost" 
                onClick={() => navigate(`/book/${chapter.book_id}`)}
                className="text-stone-600 hover:text-stone-900"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold playfair text-stone-900">
                  Chapter {chapter.chapter_number}: {chapter.title}
                </h1>
                <p className="text-sm text-stone-600">{chapter.word_count} words</p>
              </div>
            </div>
            <div className="flex gap-2">
              {!editing && (
                <>
                  <Button 
                    data-testid="regenerate-chapter-btn"
                    onClick={handleRegenerate}
                    disabled={regenerating}
                    variant="outline"
                    className="border-stone-300"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${regenerating ? 'animate-spin' : ''}`} />
                    {regenerating ? "Regenerating..." : "Regenerate"}
                  </Button>
                  <Button 
                    data-testid="edit-chapter-btn"
                    onClick={() => setEditing(true)}
                    className="bg-amber-900 hover:bg-amber-800 text-white"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                </>
              )}
              {editing && (
                <>
                  <Button 
                    data-testid="cancel-edit-btn"
                    onClick={() => {
                      setEditing(false);
                      setEditContent(chapter.content);
                    }}
                    variant="outline"
                    className="border-stone-300"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                  <Button 
                    data-testid="save-chapter-btn"
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-amber-900 hover:bg-amber-800 text-white"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? "Saving..." : "Save"}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        <Card className="max-w-4xl mx-auto border-stone-200 shadow-lg" data-testid="chapter-content-card">
          <CardHeader>
            <CardTitle className="text-2xl playfair text-stone-900">
              Chapter {chapter.chapter_number}: {chapter.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {editing ? (
              <Textarea
                data-testid="chapter-editor"
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="min-h-[600px] font-serif text-base leading-relaxed"
              />
            ) : (
              <div className="prose prose-stone max-w-none" data-testid="chapter-content">
                {chapter.content.split('\n\n').map((paragraph, idx) => (
                  paragraph.trim() && (
                    <p key={idx} className="mb-4 text-stone-800 leading-relaxed text-base">
                      {paragraph}
                    </p>
                  )
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ChapterView;